import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeHref } from '@evo-hub/shared';
import { Button } from '../../ui/Button.js';
import { IconButton } from '../../ui/IconButton.js';
import { Icon } from '../../Icon.js';
import { Input } from '../../ui/Input.js';
import { Select } from '../../ui/Select.js';
import { PanelSection, PanelToolbar } from '../../ui/PanelSection.js';
import { Pill } from '../../PillGroup.js';
import type { IconName } from '../../Icon.js';
import type { GroupState } from './inspector.js';
import type { PageEditorState } from './usePageEditorState.js';
import { StyleSection } from './StyleSection.js';

// ── Inspector de propiedades, dentro del panel de Evo Brain ──────────────────
//
// Los controles NO son una lista fija: salen de las utilities que tiene el
// template del proyecto. Medido sobre los 5: solar y lumen no tienen ninguna
// utility de padding, property no tiene ninguna de tamaño. Un grupo sin opciones
// se dice, no se disimula con un control deshabilitado — enterarse de que la
// propiedad no aplica es mejor que terminar con la página despegada del sistema.
//
// Cada valor muestra de dónde sale: del template o de una edición manual. Sin
// eso, en un mes una clase en el HTML no dice quién la puso.

export interface InspectorSectionProps {
  editor: PageEditorState;
}

/** Etiqueta de procedencia. Solo se muestra cuando hay algo que aclarar. */
function OriginTag({ origin }: { origin: GroupState['origin'] }) {
  const { t } = useTranslation();
  if (origin === 'manual') {
    return (
      <Pill variant="outline" tone="cyan" size="sm">
        {t('projects.editor.inspector.origin.manual')}
      </Pill>
    );
  }
  return (
    <span style={{
      fontSize: 'var(--evo-fs-xs)',
      color: 'var(--evo-fg-3)',
      fontFamily: 'var(--evo-font)',
    }}>
      {t(`projects.editor.inspector.origin.${origin}`)}
    </span>
  );
}

function GroupRow({ group, onChange }: { group: GroupState; onChange: (v: string | null) => void }) {
  const { t } = useTranslation();
  const label = t(`projects.editor.inspector.group.${group.id}`);

  if (group.unavailable) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <span style={{
          fontSize: 'var(--evo-fs-xs)',
          color: 'var(--evo-fg-3)',
          fontFamily: 'var(--evo-font)',
        }}>
          {label}
        </span>
        <span style={{
          fontSize: 'var(--evo-fs-xs)',
          color: 'var(--evo-fg-3)',
          fontFamily: 'var(--evo-font)',
          opacity: 0.75,
        }}>
          {t('projects.editor.inspector.unavailable')}
        </span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--evo-space-2)' }}>
        <span style={{
          fontSize: 'var(--evo-fs-xs)',
          color: 'var(--evo-fg-3)',
          fontFamily: 'var(--evo-font)',
        }}>
          {label}
        </span>
        <OriginTag origin={group.origin} />
      </div>
      <Select<string>
        value={group.value}
        onChange={onChange}
        options={group.options.map(o => ({ value: o, label: o }))}
        placeholder={t('projects.editor.inspector.inheritedOption')}
        clearable
      />
    </div>
  );
}

/** Etiqueta de sección chica, la que llevan todos los controles del inspector. */
function FieldLabel({ children, right }: { children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--evo-space-2)' }}>
      <span style={{
        fontSize: 'var(--evo-fs-xs)',
        color: 'var(--evo-fg-3)',
        fontFamily: 'var(--evo-font)',
      }}>
        {children}
      </span>
      {right}
    </div>
  );
}

/** Texto en gris chico, para lo que el template no puede hacer. */
function NotaGris({ children }: { children: React.ReactNode }) {
  return (
    <span style={{
      fontSize: 'var(--evo-fs-xs)',
      color: 'var(--evo-fg-3)',
      fontFamily: 'var(--evo-font)',
      opacity: 0.75,
    }}>
      {children}
    </span>
  );
}

/**
 * Destino de un enlace.
 *
 * Es lo único que el editor escribe como atributo. El valor se valida MIENTRAS
 * se escribe con la misma función que usa el servidor al guardar, así un destino
 * que no sirve se dice acá y no vuelve como un error después de guardar.
 */
function LinkRow({ editor }: { editor: PageEditorState }) {
  const { t } = useTranslation();
  const { selection, setLinkHref } = editor;
  const actual = selection?.href;
  const [borrador, setBorrador] = useState(actual ?? '');

  // Al cambiar de elemento el campo muestra el destino del nuevo, no el de antes.
  useEffect(() => { setBorrador(actual ?? ''); }, [actual, selection?.targetId]);

  if (actual === undefined) return null;

  const limpio = normalizeHref(borrador);
  const invalido = borrador.trim() !== '' && limpio === null;
  const sinCambio = limpio === null || limpio === actual;

  const aplicar = (): void => {
    if (limpio === null || limpio === actual) return;
    setLinkHref(limpio);
    setBorrador(limpio);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <FieldLabel right={selection?.destinoEditadoAMano ? (
        <Pill variant="outline" tone="cyan" size="sm">
          {t('projects.editor.inspector.origin.manual')}
        </Pill>
      ) : undefined}>
        {t('projects.editor.link.label')}
      </FieldLabel>
      <Input
        value={borrador}
        onChange={setBorrador}
        size="sm"
        fullWidth
        placeholder={t('projects.editor.link.placeholder')}
        error={invalido}
        onKeyDown={e => { if (e.key === 'Enter') aplicar(); }}
      />
      {invalido && <NotaGris>{t('projects.editor.link.invalid')}</NotaGris>}
      <Button variant="outline" tone="cyan" size="sm" disabled={sinCambio} onClick={aplicar}>
        {t('projects.editor.link.apply')}
      </Button>
    </div>
  );
}

/**
 * Párrafo ↔ encabezado.
 *
 * Si el template no estila encabezados por etiqueta, se dice, igual que un grupo
 * de utilities que no existe: convertir no cambiaría nada visible y ofrecerlo
 * sería mentir.
 */
function HeadingRow({ editor }: { editor: PageEditorState }) {
  const { t } = useTranslation();
  const { selection, setTextTag } = editor;
  if (!selection) return null;

  if (selection.sinEstilosDeEncabezado) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <FieldLabel>{t('projects.editor.heading.label')}</FieldLabel>
        <NotaGris>{t('projects.editor.heading.unavailable')}</NotaGris>
      </div>
    );
  }
  if (selection.tagOptions.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <FieldLabel right={selection.etiquetaEditadaAMano ? (
        <Pill variant="outline" tone="cyan" size="sm">
          {t('projects.editor.inspector.origin.manual')}
        </Pill>
      ) : undefined}>
        {t('projects.editor.heading.label')}
      </FieldLabel>
      <Select<string>
        value={selection.tag}
        onChange={v => { if (v) setTextTag(v); }}
        options={selection.tagOptions.map(o => ({ value: o, label: t(`projects.editor.heading.${o}`) }))}
      />
    </div>
  );
}

/** Duplicar, borrar y reordenar el bloque seleccionado — todo en iconos.
 *
 *  Antes eran cuatro botones con texto ("Duplicado", "Mover hacia arriba",
 *  "Mover hacia abajo", "Borrar") que ocupaban dos filas del panel para cuatro
 *  acciones que cualquier programa de diseño resuelve con cuatro iconos. El
 *  texto sigue estando: es el tooltip y el aria-label, así que no se pierde
 *  para quien navega con lector de pantalla ni para quien duda. */
function BlockRow({ editor }: { editor: PageEditorState }) {
  const { t } = useTranslation();
  const { selection, duplicateBlock, removeBlock, moveBlock } = editor;
  if (!selection) return null;

  return (
    <PanelToolbar>
      <IconButton
        icon={<Icon name="copy" size={15} />} ariaLabel={t('projects.editor.block.duplicate')} tooltipAlign="left"
        variant="outline" tone="muted" size="sm" onClick={duplicateBlock}
      />
      <IconButton
        icon={<Icon name="arrow-up" size={15} />} ariaLabel={t('projects.editor.block.moveUp')}
        variant="outline" tone="muted" size="sm"
        disabled={!selection.puedeSubir} onClick={() => { moveBlock('up'); }}
      />
      <IconButton
        icon={<Icon name="arrow-down" size={15} />} ariaLabel={t('projects.editor.block.moveDown')}
        variant="outline" tone="muted" size="sm"
        disabled={!selection.puedeBajar} onClick={() => { moveBlock('down'); }}
      />
      <IconButton
        icon={<Icon name="trash" size={15} />} ariaLabel={t('projects.editor.block.remove')}
        variant="outline" tone="danger" size="sm" onClick={removeBlock}
      />
    </PanelToolbar>
  );
}

/** Reemplazar la imagen seleccionada, desde la imagen y no desde el cajón. */
function ImageRow({ editor }: { editor: PageEditorState }) {
  const { t } = useTranslation();
  const { selection, replaceImage, isUploadingImage } = editor;
  const inputRef = useRef<HTMLInputElement | null>(null);
  if (!selection?.imageFilename) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <span style={{
        fontSize: 'var(--evo-fs-xs)',
        color: 'var(--evo-fg-3)',
        fontFamily: 'var(--evo-font)',
        wordBreak: 'break-all',
      }}>
        {t('projects.editor.image.current', { filename: selection.imageFilename })}
      </span>
      {/* El <input type=file> va oculto y se dispara desde el Button del sistema:
          un input de archivo nativo no tiene los estilos del design system. */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) replaceImage(file);
          e.target.value = '';
        }}
      />
      <Button
        variant="outline"
        tone="cyan"
        size="sm"
        loading={isUploadingImage}
        onClick={() => { inputRef.current?.click(); }}
      >
        {t('projects.editor.image.replace')}
      </Button>
    </div>
  );
}

/** Icono de cada acción de formato. `lineBreak` no tiene un glifo obvio en
 *  ningún set: la flecha de retorno es lo que usa todo el mundo. */
const FORMAT_ICON: Record<'bold' | 'lineBreak' | 'bullets', IconName> = {
  bold: 'bold',
  lineBreak: 'line-break',
  bullets: 'list-bullet',
};

export function InspectorSection({ editor }: InspectorSectionProps) {
  const { t } = useTranslation();
  const { selection, setGroupValue, applyFormat, canFormat, formatActions } = editor;
  // El alcance se recuerda mientras dura la sesión de edición: quien está
  // arreglando la versión mobile de una página va a tocar varias cosas seguidas,
  // y volver a "todas" en cada elemento sería tener que acordarse cada vez.
  const [styleScope, setStyleScope] = useState<'all' | 'current'>('all');

  if (!selection) {
    return (
      <span style={{
        fontSize: 'var(--evo-fs-xs)',
        color: 'var(--evo-fg-3)',
        fontFamily: 'var(--evo-font)',
        padding: 'var(--evo-space-2) 0',
      }}>
        {t('projects.editor.inspector.nothingSelected')}
      </span>
    );
  }

  const tieneImagen = selection.imageFilename != null;
  const tieneEnlace = selection.href !== undefined;
  const tieneTexto = canFormat || selection.tagOptions.length > 0 || selection.sinEstilosDeEncabezado;

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {tieneTexto && (
        <PanelSection id="editor-texto" title={t('projects.editor.section.text')}>
          {canFormat && (
            <PanelToolbar>
              {formatActions.map(accion => (
                <IconButton
                  key={accion}
                  icon={<Icon name={FORMAT_ICON[accion]} size={15} />}
                  ariaLabel={t(`projects.editor.format.${accion}`)}
                  tooltipAlign="left"
                  variant="outline"
                  tone="muted"
                  size="sm"
                  // Sin esto el botón toma el foco al apretarlo y la selección
                  // dentro del iframe se pierde: el formato iría sobre nada.
                  onMouseDown={e => { e.preventDefault(); }}
                  onClick={() => { applyFormat(accion); }}
                />
              ))}
            </PanelToolbar>
          )}
          <HeadingRow editor={editor} />
        </PanelSection>
      )}

      {tieneImagen && (
        <PanelSection id="editor-imagen" title={t('projects.editor.section.image')}>
          <ImageRow editor={editor} />
        </PanelSection>
      )}

      {tieneEnlace && (
        <PanelSection id="editor-enlace" title={t('projects.editor.section.link')}>
          <LinkRow editor={editor} />
        </PanelSection>
      )}

      <PanelSection id="editor-ajustes" title={t('projects.editor.section.style')}>
        <StyleSection editor={editor} scope={styleScope} onScopeChange={setStyleScope} />
      </PanelSection>

      <PanelSection id="editor-bloque" title={t('projects.editor.section.block')}>
        <BlockRow editor={editor} />
      </PanelSection>

      <PanelSection
        id="editor-propiedades"
        title={t('projects.editor.section.properties', { tag: selection.tag })}
        titleRight={selection.textoEditadoAMano ? (
          <Pill variant="outline" tone="cyan" size="sm">
            {t('projects.editor.inspector.textEdited')}
          </Pill>
        ) : undefined}
      >
        {selection.groups.length === 0
          ? <NotaGris>{t('projects.editor.inspector.unavailable')}</NotaGris>
          : selection.groups.map(g => (
            <GroupRow key={g.id} group={g} onChange={v => { setGroupValue(g, v); }} />
          ))}
      </PanelSection>
    </div>
  );
}
